import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LOCALE_NAMES, SUPPORTED_LOCALES } from '@zt/i18n';
import { Button, Field, Input, Section, Select, Spinner, Textarea } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { Organization } from '../lib/types';

const CURRENCIES = ['TRY', 'EUR', 'USD', 'GBP', 'AED', 'SAR', 'AZN'];
type Form = Record<
  | 'name'
  | 'legalName'
  | 'taxOffice'
  | 'taxNumber'
  | 'mersisNo'
  | 'kepAddress'
  | 'etbisUrl'
  | 'phone'
  | 'email'
  | 'address'
  | 'defaultLocale'
  | 'defaultCurrency'
  | 'systemName',
  string
>;

export function OrganizationPage() {
  const t = useT();
  const { can } = usePermissions();
  const editable = can('org.settings.update');
  const q = useQuery({
    queryKey: ['organization'],
    queryFn: () => api.get<Organization>('/v1/admin/organization'),
  });
  const [form, setForm] = useState<Form | null>(null);

  useEffect(() => {
    if (q.data) {
      const o = q.data;
      setForm({
        name: o.name,
        legalName: o.legalName ?? '',
        taxOffice: o.taxOffice ?? '',
        taxNumber: o.taxNumber ?? '',
        mersisNo: o.mersisNo ?? '',
        kepAddress: o.kepAddress ?? '',
        etbisUrl: o.etbisUrl ?? '',
        phone: o.phone ?? '',
        email: o.email ?? '',
        address: o.address ?? '',
        defaultLocale: o.defaultLocale,
        defaultCurrency: o.defaultCurrency,
        systemName: o.systemName ?? '',
      });
    }
  }, [q.data]);

  const save = useApiMutation(
    (f: Form) => {
      const nul = (v: string) => (v.trim() === '' ? null : v.trim());
      return api.patch<Organization>(
        '/v1/admin/organization',
        {
          name: f.name.trim(),
          legalName: nul(f.legalName),
          taxOffice: nul(f.taxOffice),
          taxNumber: nul(f.taxNumber),
          mersisNo: nul(f.mersisNo),
          kepAddress: nul(f.kepAddress),
          etbisUrl: nul(f.etbisUrl),
          phone: nul(f.phone),
          email: nul(f.email),
          address: nul(f.address),
          defaultLocale: f.defaultLocale,
          defaultCurrency: f.defaultCurrency,
          systemName: nul(f.systemName),
        },
        { ifMatch: q.data?.version },
      );
    },
    {
      // Panel adı (F12-12) oturum bilgisinden okunur: kaydedince başlık ve menü hemen güncellenir.
      invalidate: [['organization'], ['setup'], ['session']],
      success: t('common.saved'),
      silentFieldErrors: true,
    },
  );

  if (!form)
    return (
      <PageFrame helpKey="page.organization">
        <Spinner label={t('common.loading')} />
      </PageFrame>
    );
  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setForm({ ...form, [k]: e.target.value });
  const err = (f: string) => fieldError(save.error, f);
  const opt = t('common.optional');

  return (
    <PageFrame helpKey="page.organization">
      <form
        className="space-y-8"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate(form);
        }}
      >
        <fieldset disabled={!editable} className="space-y-8">
          <Section
            title={t('admin.organization.legalSection')}
            description={t('admin.organization.legalLead')}
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={t('admin.organization.name')} required error={err('name')}>
                {(p) => <Input {...p} value={form.name} onChange={set('name')} />}
              </Field>
              <Field label={t('admin.organization.legalName')} optionalLabel={opt} error={err('legalName')}>
                {(p) => <Input {...p} value={form.legalName} onChange={set('legalName')} />}
              </Field>
              <Field label={t('admin.organization.taxOffice')} optionalLabel={opt} error={err('taxOffice')}>
                {(p) => <Input {...p} value={form.taxOffice} onChange={set('taxOffice')} />}
              </Field>
              <Field
                label={t('admin.organization.taxNumber')}
                optionalLabel={opt}
                error={err('taxNumber')}
                help={<HelpTip k="organization.tax_number" />}
              >
                {(p) => (
                  <Input
                    {...p}
                    inputMode="numeric"
                    className="tabular"
                    value={form.taxNumber}
                    onChange={set('taxNumber')}
                  />
                )}
              </Field>
              <Field
                label={t('admin.organization.mersis')}
                optionalLabel={opt}
                error={err('mersisNo')}
                help={<HelpTip k="organization.mersis" />}
              >
                {(p) => (
                  <Input
                    {...p}
                    inputMode="numeric"
                    className="tabular"
                    value={form.mersisNo}
                    onChange={set('mersisNo')}
                  />
                )}
              </Field>
              <Field
                label={t('admin.organization.kep')}
                optionalLabel={opt}
                error={err('kepAddress')}
                help={<HelpTip k="organization.kep" />}
              >
                {(p) => <Input {...p} type="email" value={form.kepAddress} onChange={set('kepAddress')} />}
              </Field>
              <Field
                label={t('admin.organization.etbis')}
                description={t('admin.organization.etbisHint')}
                optionalLabel={opt}
                error={err('etbisUrl')}
                help={<HelpTip k="organization.etbis" />}
                className="sm:col-span-2"
              >
                {(p) => (
                  <Input
                    {...p}
                    type="url"
                    inputMode="url"
                    placeholder="https://etbis.eticaret.gov.tr/sitedogrulama/…"
                    value={form.etbisUrl}
                    onChange={set('etbisUrl')}
                  />
                )}
              </Field>
            </div>
          </Section>
          <Section title={t('admin.organization.contactSection')}>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={t('admin.organization.phone')} optionalLabel={opt} error={err('phone')}>
                {(p) => <Input {...p} type="tel" value={form.phone} onChange={set('phone')} />}
              </Field>
              <Field label={t('admin.organization.email')} optionalLabel={opt} error={err('email')}>
                {(p) => <Input {...p} type="email" value={form.email} onChange={set('email')} />}
              </Field>
              <Field
                label={t('admin.organization.address')}
                optionalLabel={opt}
                error={err('address')}
                className="sm:col-span-2"
              >
                {(p) => <Textarea {...p} value={form.address} onChange={set('address')} />}
              </Field>
            </div>
          </Section>
          <Section title={t('admin.organization.defaultsSection')}>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={t('admin.organization.defaultLocale')} required>
                {(p) => (
                  <Select {...p} value={form.defaultLocale} onChange={set('defaultLocale')}>
                    {SUPPORTED_LOCALES.map((l) => (
                      <option key={l} value={l}>
                        {LOCALE_NAMES[l]}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label={t('admin.organization.defaultCurrency')} required>
                {(p) => (
                  <Select {...p} value={form.defaultCurrency} onChange={set('defaultCurrency')}>
                    {CURRENCIES.map((c) => (
                      <option key={c}>{c}</option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field
                label={t('admin.organization.systemName')}
                description={t('admin.organization.systemNameHint')}
                help={<HelpTip k="organization.system_name" />}
              >
                {(p) => (
                  <Input
                    {...p}
                    value={form.systemName}
                    onChange={set('systemName')}
                    maxLength={60}
                    placeholder={t('admin.auth.brand')}
                  />
                )}
              </Field>
            </div>
          </Section>
        </fieldset>
        {editable && (
          <div className="zt-sticky-bottom sticky bottom-0 -mx-4 flex items-center justify-end gap-2 border-t border-line bg-paper/95 px-4 py-3 backdrop-blur sm:mx-0 sm:px-0">
            <HelpTip k="organization.save" />
            <Button type="submit" variant="primary" loading={save.isPending}>
              {t('common.save')}
            </Button>
          </div>
        )}
      </form>
    </PageFrame>
  );
}
